import React, { useState, useEffect } from 'react';
import { Github, Linkedin, Mail, FileText, MapPin, Trophy, Briefcase, GraduationCap } from 'lucide-react';

const F1Portfolio = () => {
  const [startSequence, setStartSequence] = useState(0);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setStartSequence(prev => {
        if (prev < 5) {
          return prev + 1;
        } else {
          clearInterval(timer);
          // Pause for 0.6 seconds after countdown before showing content
          setTimeout(() => {
            setShowContent(true);
          }, 600);
          return prev;
        }
      });
    }, 145);

    return () => clearInterval(timer);
  }, []);

  const achievements = [
    {
      id: 1,
      title: "Computer Science Degree",
      type: "education",
      year: "Aug 2019 - May 2023",
      description: "BS Computer Science - Binghamton University, Cum Laude (3.55 GPA)",
      position: { top: "9%", left: "84%" } // Turn 1 - cx="1070.951" cy="-262.842"
    },
    {
      id: 2,
      title: "KonoReplayer Project",
      type: "project",
      year: "Dec 2021 - Feb 2022",
      description: "Android automation system using OpenCV and image recognition",
      
      position: { top: "14%", left: "78.5%" } // Turn 2 - cx="1054.957" cy="-253.199"
    },
    {
      id: 3,
      title: "MyMovieMania Project",
      type: "project",
      year: "Feb 2022 - May 2022",
      description: "Full-stack Flask web app with Docker containerization for movie watchlists",
      position: { top: "25%", left: "80.3%" } // Turn 3 - cx="1060.809" cy="-231.769"
    },
    {
      id: 4,
      title: "Software Engineer Intern",
      type: "work",
      year: "Jun 2022 - Aug 2022",
      description: "BNY - Built Go services to reducing process calculation time by 95%",
      position: { top: "38%", left: "79.7%" } // Turn 4 - cx="1058.047" cy="-205.909"
    },
    {
      id: 5,
      title: "Software Engineer I",
      type: "work",
      year: "Aug 2023 - March 2025",
      description: "BNY - Architected scalable Java/Spring Boot backend services",
      position: { top: "39%", left: "35%" } // Turn 5 - cx="915.595" cy="-204.64"
    },
    {
      id: 6,
      title: "Software Engineer II",
      type: "work",
      year: "Mar 2025 - Current",
      description: "BNY - Engineered event-driven notification system with WebSockets",
      position: { top: "14%", left: "37.2%" } // Turn 6 - cx="923.397" cy="-253.3"
    },
    {
      id: 7,
      title: "LinkedInNext",
      type: "project",
      year: "Aug 2025 - Current",
      link: "https://github.com/yliu401/linkedinNext",
      description: "Browser Extension for a stationary next button for Linkedin Job board",
      position: { top: "8%", left: "24.4%" } 
    },
  ];

  const getAchievementIcon = (type) => {
    switch (type) {
      case 'education':
        return <GraduationCap className="w-4 h-4" />;
      case 'work':
        return <Briefcase className="w-4 h-4" />;
      case 'project':
        return <Trophy className="w-4 h-4" />;
      default:
        return <MapPin className="w-4 h-4" />;
    }
  };

  const StartLights = ({ lightsOut }) => (
    <div className="flex justify-center items-center space-x-8 mb-12">
      {[1, 2, 3, 4, 5].map((light) => (
        <div
          key={light}
          className={`w-16 h-24 rounded-full border-4 transition-all duration-750 ${
            startSequence >= 5
              ? 'bg-green-500 border-green-300 shadow-lg shadow-green-500/50'
              : startSequence >= light
              ? 'bg-[#b4ae98] border-[#66706a] shadow-lg shadow-[#b4ae98]/50'
              : 'bg-[#3c4a4c] border-[#66706a]'
          }`}
        />
      ))}
    </div>
  );

  if (!showContent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#3c4a4c] via-black to-[#66706a] flex flex-col justify-center items-center text-white">
        {/* <div className="text-6xl font-bold mb-8 font-mono tracking-wider">
          YONG LIU PORTFOLIO
        </div> */}
        <StartLights />
        <div className="text-2xl font-semibold animate-pulse">
          {startSequence < 5 ? 'GET READY...' : 'LIGHTS OUT AND AWAY WE GO!'}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#3c4a4c] via-black to-[#66706a] text-white">
      {/* Header */}
      <header className="relative py-12 text-center border-b border-[#b4ae98]/30 bg-[#3c4a4c]/20">
        {/* Profile Image Section */}
        <div className="relative z-10 mb-8">
          <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-r from-[#b4ae98] to-[#66706a] p-1 shadow-2xl shadow-[#b4ae98]/30">
          <div className="w-full h-full rounded-full bg-[#3c4a4c] flex items-center justify-center overflow-hidden">
            {/* Replace with your actual profile image */}
            <img 
              src="profile-image.jpg" 
              alt="Yong Liu" 
              className="w-full h-full object-cover"
            />
            {/* Fallback User icon if image fails to load */}
            {/* <User className="w-16 h-16 text-[#b4ae98]" /> */}
            </div>
          </div>
        </div>
        
        <h1 className="text-6xl font-bold mb-4 font-mono tracking-wider relative z-10">
          YONG LIU
        </h1>
        <p className="text-2xl text-[#b4ae98] font-semibold relative z-10">
          SOFTWARE ENGINEER II • 2+ YEARS OF EXPERIENCE
        </p>
        <div className="mt-8 flex justify-center space-x-6 relative z-10">
          {[
            { icon: <Github className="w-6 h-6" />, label: "GitHub", href: "https://github.com/yliu401" },
            { icon: <Linkedin className="w-6 h-6" />, label: "LinkedIn", href: "https://www.linkedin.com/in/yliu401" },
            { icon: <Mail className="w-6 h-6" />, label: "Email", href: "mailto:yongliu1212@gmail.com" },
            { icon: <FileText className="w-6 h-6" />, label: "Resume", href: "/resume.pdf", download: "Yong_Liu_Resume.pdf" }
            ].map((link, index) => (
            <a
              key={index}
              href={link.href}
              download={link.download || undefined}
              className="bg-[#b4ae98] hover:bg-[#66706a] p-4 rounded-full transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-[#b4ae98]/50 group"
            >
              {link.icon}
              <span className="sr-only">{link.label}</span>
            </a>
          ))}
        </div>
      </header>

      {/* About Section */}
      <section className="py-12 px-8 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-6 font-mono">ABOUT ME</h2>
          <div className="w-24 h-1 bg-[#b4ae98] mx-auto mb-8"></div>
        </div>
        
        <div className="bg-[#3c4a4c]/50 backdrop-blur-sm p-8 rounded-lg border border-[#b4ae98]/30 shadow-2xl">
          <p className="text-lg leading-relaxed text-gray-300">
          I’m a Software Engineer at BNY with experience building scalable backend services and real-time frontends that streamline critical business workflows. My background spans developing Java/Spring Boot microservices, Angular micro frontends, and high-performance systems in Go, with a strong focus on event-driven architectures and system efficiency. I enjoy solving complex problems end-to-end—whether designing APIs or exploring new techonologies —and have delivered solutions that improved performance and reliability across thousands of daily transactions. Outside of work, I explore personal projects for random niche issues, running, boxing, and continuously pushing myself to learn and grow.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="text-center p-6 bg-[#3c4a4c]/50 rounded-lg">
              <div className="text-3xl font-bold text-[#b4ae98]">4+</div>
              <div className="text-gray-400">Major Projects</div>
            </div>
            <div className="text-center p-6 bg-[#3c4a4c]/50 rounded-lg">
              <div className="text-3xl font-bold text-[#b4ae98]">10+</div>
              <div className="text-gray-400">Technologies Proficient Across</div>
            </div>
            <div className="text-center p-6 bg-[#3c4a4c]/50 rounded-lg">
              <div className="text-3xl font-bold text-[#b4ae98]">2+</div>
              <div className="text-gray-400">Years of Experience</div>
            </div>
          </div>
        </div>
      </section>
       {/* Skills Section */}
       <section className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-6 font-mono">TECH STACK</h2>
          <div className="w-24 h-1 bg-[#b4ae98] mx-auto mb-8"></div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
          {[
            { 
              name: "Java", 
              category: "Backend", 
              color: "#f89820",
              icon: (
                <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
                  <path d="M8.851 18.56s-.917.534.653.714c1.902.218 2.874.187 4.969-.211 0 0 .552.346 1.321.646-4.699 2.013-10.633-.118-6.943-1.149M8.276 15.933s-1.028.761.542.924c2.032.209 3.636.227 6.413-.308 0 0 .384.389.987.602-5.679 1.661-12.007.13-7.942-1.218M13.116 11.475c1.158 1.333-.304 2.533-.304 2.533s2.939-1.518 1.589-3.418c-1.261-1.772-2.228-2.652 3.007-5.688 0-.001-8.216 2.051-4.292 6.573M19.33 20.504s.679.559-.747.991c-2.712.822-11.288 1.069-13.669.033-.856-.373.75-.89 1.254-.998.527-.114.828-.093.828-.093-.953-.671-6.156 1.317-2.643 1.887 9.58 1.553 17.462-.7 14.977-1.82M9.292 13.21s-4.362 1.036-1.544 1.412c1.189.159 3.561.123 5.77-.062 1.806-.152 3.618-.477 3.618-.477s-.637.272-1.098.587c-4.429 1.165-12.986.623-10.522-.568 2.082-1.006 3.776-.892 3.776-.892M17.116 17.584c4.503-2.34 2.421-4.589.968-4.285-.355.074-.515.138-.515.138s.132-.207.385-.297c2.875-1.011 5.086 2.981-.928 4.562 0-.001.07-.062.09-.118M14.401 0s2.494 2.494-2.365 6.33c-3.896 3.077-.888 4.832-.001 6.836-2.274-2.053-3.943-3.858-2.824-5.539 1.644-2.469 6.197-3.665 5.19-7.627M9.734 23.924c4.322.277 10.959-.153 11.116-2.198 0 0-.302.775-3.572 1.391-3.688.694-8.239.613-10.937.168 0-.001.553.457 3.393.639"/>
                </svg>
              )
            },
            { 
              name: "Spring Boot", 
              category: "Framework", 
              color: "#6db33f",
              icon: (
                <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
                  <path d="M21.8537 1.4158a10.4504 10.4504 0 0 1-1.284 2.2471A11.9666 11.9666 0 1 0 3.8518 20.7757l.4445-.3951a19.9545 19.9545 0 0 1 8.1559-7.6247c.4485-.2061.9063-.3858 1.3726-.5369a12.0274 12.0274 0 0 1 .2674-.0805 11.9988 11.9988 0 0 0-10.806-6.8618L2.7667 5.394a12.0052 12.0052 0 0 1 .9341-.0308 11.9682 11.9682 0 0 1 10.3069 5.9149c.2674.4406.5053.8946.7144 1.3623-.004-.0806-.0079-.1611-.0118-.2418a9.1357 9.1357 0 0 0-.1879-1.6235 11.9725 11.9725 0 0 1-.0648-7.5398 10.8792 10.8792 0 0 1 7.3961-2.2746zm-10.3152 8.2952a7.1103 7.1103 0 0 0-.8675 3.5637c.0157.3118.0511.6196.1056.9234.0228.1265.0496.2511.0783.3757.1225.5329.2968 1.0498.5211 1.5431a7.1124 7.1124 0 0 0 7.1124-7.1124 7.1124 7.1124 0 0 0-7.1124-7.1124c-.3961 0-.7842.0354-1.1654.0985 2.8317 1.2833 3.0551 3.4945 1.3279 5.7204zm.5014-2.1832a1.8784 1.8784 0 1 1-3.7568 0 1.8784 1.8784 0 0 1 3.7568 0zM12 19.2a7.2 7.2 0 1 1 0-14.4 7.2 7.2 0 0 1 0 14.4z"/>
                </svg>
              )
            },
            { 
              name: "Angular", 
              category: "Frontend", 
              color: "#dd0031",
              icon: (
                <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
                  <path d="M9.93 12.645h4.14l-2.07-4.324-2.07 4.324zm-2.96 5.925L12 2.5l5.03 16.07H14.97L13.94 16.07H10.06l-1.03 2.5H7.03zm14.97.93L12 0 2.07 19.5h3.47L12 5.74l6.46 13.76h3.47z"/>
                </svg>
              )
            },
            { 
              name: "TypeScript", 
              category: "Language", 
              color: "#3178c6",
              icon: (
                <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
                  <path d="M1.125 0C.502 0 0 .502 0 1.125v21.75C0 23.498.502 24 1.125 24h21.75c.623 0 1.125-.502 1.125-1.125V1.125C24 .502 23.498 0 22.875 0zm17.363 9.75c.612 0 1.154.037 1.627.111a6.38 6.38 0 0 1 1.306.34v2.458a3.95 3.95 0 0 0-.643-.361 5.093 5.093 0 0 0-.717-.26 5.453 5.453 0 0 0-1.426-.2c-.3 0-.573.028-.819.086a2.1 2.1 0 0 0-.623.242c-.17.104-.3.229-.393.374a.888.888 0 0 0-.14.49c0 .196.053.373.156.529.104.156.252.304.443.444s.423.276.696.41c.273.135.582.274.926.416.47.197.892.407 1.266.628.374.222.695.473.963.753.268.279.472.598.614.957.142.359.214.776.214 1.253 0 .657-.125 1.21-.373 1.656a3.033 3.033 0 0 1-1.012 1.085 4.38 4.38 0 0 1-1.487.596c-.566.12-1.163.18-1.79.18a9.916 9.916 0 0 1-1.84-.164 5.544 5.544 0 0 1-1.512-.493v-2.63a5.033 5.033 0 0 0 3.237 1.2c.333 0 .624-.03.872-.09.249-.06.456-.144.623-.25.166-.108.29-.234.373-.38a1.023 1.023 0 0 0-.074-1.089 2.12 2.12 0 0 0-.537-.5 5.597 5.597 0 0 0-.807-.444 27.72 27.72 0 0 0-1.007-.436c-.918-.383-1.602-.852-2.053-1.405-.45-.553-.676-1.222-.676-2.005 0-.614.123-1.141.369-1.582.246-.441.58-.804 1.004-1.089a4.494 4.494 0 0 1 1.47-.629 7.536 7.536 0 0 1 1.77-.201zm-15.113.188h9.563v2.166H9.506v9.646H6.789v-9.646H3.375z"/>
                </svg>
              )
            },
            { 
              name: "Go", 
              category: "Backend", 
              color: "#00add8",
              icon: (
                <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
                  <path d="M1.811 10.231c-.047 0-.058-.023-.035-.059l.246-.315c.023-.035.081-.058.128-.058h4.172c.046 0 .058.035.035.07l-.199.303c-.023.036-.082.059-.117.059H1.811zM.047 11.306c-.047 0-.059-.023-.035-.058l.245-.316c.023-.035.082-.058.129-.058h5.516c.047 0 .07.035.058.07l-.093.28c-.012.047-.058.07-.105.07H.047zM2.828 12.38c-.047 0-.059-.035-.035-.07l.163-.292c.023-.035.07-.07.117-.07h2.337c.047 0 .07.035.07.082l-.023.28c0 .047-.047.082-.082.082H2.828zM21.918 12.367a12.19 12.19 0 0 0-.645-.88c-.75-.992-1.805-1.842-3.072-2.497-1.302-.68-2.741-1.026-4.326-1.026-1.471 0-2.78.28-3.976.82a8.368 8.368 0 0 0-2.869 2.226c-.75.937-1.314 2.016-1.675 3.237-.375 1.267-.398 2.544-.07 3.822.352 1.267.97 2.346 1.827 3.237.855.88 1.969 1.56 3.33 2.029 1.382.48 2.87.48 4.462 0a9.291 9.291 0 0 0 3.33-2.029c.855-.89 1.475-1.97 1.827-3.237.328-1.278.305-2.555-.07-3.822a7.963 7.963 0 0 0-.063-.08zm-4.918 4.184c-.281.328-.633.598-1.055.809-.422.211-.867.316-1.336.316-.469 0-.914-.105-1.336-.316a3.014 3.014 0 0 1-1.055-.809c-.281-.328-.493-.715-.633-1.16a4.225 4.225 0 0 1-.211-1.324c0-.469.07-.914.211-1.324.14-.445.352-.832.633-1.16.281-.328.633-.598 1.055-.809.422-.211.867-.316 1.336-.316.469 0 .914.105 1.336.316.422.211.773.48 1.055.809.281.328.492.715.632 1.16.14.41.211.855.211 1.324 0 .469-.07.914-.211 1.324-.14.445-.351.832-.632 1.16zM16.93 16.695c-.211.211-.469.316-.773.316s-.562-.105-.773-.316c-.211-.211-.316-.469-.316-.773s.105-.563.316-.773c.211-.211.469-.316.773-.316s.562.105.773.316c.211.211.316.469.316.773s-.105.562-.316.773z"/>
                </svg>
              )
            },
            { 
              name: "SQL", 
              category: "Database", 
              color: "#336791",
              icon: (
                <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
                  <path d="M12 1C5.925 1 1 2.791 1 5v14c0 2.209 4.925 4 11 4s11-1.791 11-4V5c0-2.209-4.925-4-11-4zm0 2c5.523 0 9 1.343 9 2s-3.477 2-9 2-9-1.343-9-2 3.477-2 9-2zM3 7.5c1.134.688 4.268 1.5 9 1.5s7.866-.812 9-1.5V10c0 .657-3.477 2-9 2s-9-1.343-9-2V7.5zm0 5c1.134.688 4.268 1.5 9 1.5s7.866-.812 9-1.5V15c0 .657-3.477 2-9 2s-9-1.343-9-2v-2.5zm0 5c1.134.688 4.268 1.5 9 1.5s7.866-.812 9-1.5V19c0 .657-3.477 2-9 2s-9-1.343-9-2v-2.5z"/>
                </svg>
              )
            }
          ].map((skill, index) => (
            <div
              key={index}
              className="group relative bg-[#3c4a4c]/50 backdrop-blur-sm p-6 rounded-lg border border-[#b4ae98]/30 hover:border-[#b4ae98]/70 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-lg hover:shadow-[#b4ae98]/30"
            >
              <div className="text-center">
                <div 
                  className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: skill.color }}
                >
                  {skill.icon}
                </div>
                <h3 className="font-semibold text-white mb-1 text-sm">{skill.name}</h3>
                <p className="text-xs text-gray-400">{skill.category}</p>
              </div>
              
              {/* Racing stripe effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#b4ae98]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
            </div>
          ))}
        </div>
      </section>
      {/* Circuit Achievement Map */}
      <section className="py-12 px-8 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-6 font-mono">CAREER CIRCUIT</h2>
          <div className="w-24 h-1 bg-[#b4ae98] mx-auto mb-8"></div>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Follow my journey through the twists and turns of software engineering after graduating Binghamton University and beyond, 
            marking key achievements and milestones.
          </p>
        </div>

        {/* Las Vegas Circuit Display */}
        <div className="relative bg-[#3c4a4c]/30 rounded-xl p-8 mb-8 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#b4ae98]/10 to-transparent"></div>
          
          {/* Circuit Background */}
          <div className="relative w-full max-w-4xl mx-auto">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-[#b4ae98] font-mono">LAS VEGAS STREET CIRCUIT</h3>
              <p className="text-gray-400 text-sm mt-2">Career Journey Timeline - Hover over markers to explore achievements</p>
            </div>
            
            {/* SVG Circuit Track */}
            <div className="relative w-full h-[580px] mx-auto">
              {/* Las Vegas Circuit SVG */}
              <svg viewBox="0 0 319.083 216.771" className="w-full h-auto max-w-4xl mx-auto">
                <defs>
                  <pattern patternUnits="userSpaceOnUse" width="18.181" height="18.138" patternTransform="translate(763.477 1693.907)scale(.17379)" id="checkerboard">
                    <path style={{opacity: 1, fill: '#000', fillOpacity: 1, stroke: 'none', strokeWidth: 0.5}} d="M1025.535 1215.281h18.173v18.096h-18.173z" transform="translate(-1025.535 -1215.281)"/>
                    <path style={{opacity: 1, fill: '#fff', fillOpacity: 1, stroke: 'none', strokeWidth: 0.5}} d="M1025.535 1215.281h9.075v9.075h-9.075zm9.106 9.063h9.075v9.075h-9.075z" transform="translate(-1025.535 -1215.281)"/>
                  </pattern>
                </defs>
                <g transform="matrix(1.0042 0 0 1.0747 -808.259 300.191)">
                  {/* Main track outline */}
                  <path 
                    style={{fill: '#66706a', fillOpacity: 0.3, stroke: '#b4ae98', strokeWidth: 2.237, strokeOpacity: 0.8}} 
                    d="M948.332-96.744c-22-.116-28.05-.255-31.598-.722-9.76-1.285-20.146-4.823-34.128-11.625-4.518-2.197-13.151-6.742-23.433-12.335-4.213-2.291-9.228-5.503-18.58-11.897-9.44-6.453-13.805-8.783-18.815-10.042-2.11-.53-3.347-1.346-3.347-2.208 0-.953 2.58-11.644 3.839-15.905 1.711-5.795 2.817-7.738 5.877-10.323 2.984-2.521 7.134-4.562 14.37-7.068 10.754-3.724 20.067-9.95 22.687-15.168 1.48-2.948 2.328-6.757 2.876-12.919.118-1.334.204-10.158.246-25.474.235-28.255-.356-26.726.95-25.151-1.69-.776-.045-2.344 4.567.626 3.614 2.368 7.18 3.768 8.322 4.03 2.504.577 5.093-.698 5.667-4.6.378-1.53 1.731-2.03 5.504-2.033 2.615-.002 3.441.086 6.432.685 9.02 1.809 16.024 7.697 18.438 15.502.983 3.177.99 3.273 1.142 16.469.076 6.677.19 12.446.252 12.818.247 1.491.912 2.172 2.347 2.403.71.114 6.506.063 19.171-.17 99.966-1.836 109.141-2.025 111.968-2.303 2.532-.25 2.644.127 5.031-.66 3.516-1.156 5.902-2.517 8.106-4.621 3.757-3.588 4.693-6.758 3.245-10.982-.928-2.706-1.631-3.92-4.768-9.654-2.33-4.26-2.598-5.007-2.871-6.228-.594-2.648.089-5.15.895-6.556 2.285-2.84 5.797-3.57 8.028-1.869 2.38 2.006 2.316 1.513 13.786 15.543 5.866 7.827 11.063 14.845 11.55 15.594 1.752 2.697 2.886 5.398 3.577 8.517.365 1.648.368 1.773.338 13.176-.026 10.022-.987 42.175-.862 81.484.023 7.142.076 4.867-.768 6.868-.15.357-5.205 5.142-5.964 5.477-.628.367-1.248.83-1.378 1.029-.688.625-1.935 2.645-2.022 4.306-.11 2.098.036 4.684-.86 5.137-.584.295 1.823-.012-12.773.55-9.243.357-33.904.157-54.786.212s-40.722.124-44.09.152c-3.367.028-16.043-.002-28.168-.065z"
                  />
                  
                  {/* Start/Finish line */}
                  <g transform="translate(-576.524 -176.707)scale(.66764)">
                    <rect 
                      ry="0" 
                      transform="scale(1 -1)rotate(-52.009)" 
                      y="2003.227" 
                      x="1471.109" 
                      height="28.465" 
                      width="6.927" 
                      style={{fill: 'url(#checkerboard)', fillOpacity: 1, stroke: '#b4ae98', strokeWidth: 0.398}}
                    />
                  </g>
                </g>

                     {/* Track markers and turn numbers with correct positions from SVG (DO NOT REMOVE) */}
                  {/* <circle style={{fill: '#b4ae98', stroke: '#3c4a4c', strokeWidth: 0.353}} cx="1070.951" cy="-262.842" r="5"/>
                  <text y="-262.842" x="1070.951" style={{fill: '#3c4a4c', fontSize: '6px', textAnchor: 'middle', fontFamily: 'sans-serif', fontWeight: 'bold', dominantBaseline: 'central'}}>1</text>
                  
                  <circle style={{fill: '#b4ae98', stroke: '#3c4a4c', strokeWidth: 0.353}} cx="1054.957" cy="-253.199" r="5"/>
                  <text y="-253.199" x="1054.957" style={{fill: '#3c4a4c', fontSize: '6px', textAnchor: 'middle', fontFamily: 'sans-serif', fontWeight: 'bold', dominantBaseline: 'central'}}>2</text>
                  
                  <circle style={{fill: '#b4ae98', stroke: '#3c4a4c', strokeWidth: 0.353}} cx="1060.809" cy="-231.769" r="5"/>
                  <text y="-231.769" x="1060.809" style={{fill: '#3c4a4c', fontSize: '6px', textAnchor: 'middle', fontFamily: 'sans-serif', fontWeight: 'bold', dominantBaseline: 'central'}}>3</text>
                  
                  <circle style={{fill: '#b4ae98', stroke: '#3c4a4c', strokeWidth: 0.353}} cx="1058.047" cy="-205.909" r="5"/>
                  <text y="-205.909" x="1058.047" style={{fill: '#3c4a4c', fontSize: '6px', textAnchor: 'middle', fontFamily: 'sans-serif', fontWeight: 'bold', dominantBaseline: 'central'}}>4</text>
                  
                  <circle style={{fill: '#b4ae98', stroke: '#3c4a4c', strokeWidth: 0.353}} cx="915.595" cy="-204.64" r="5"/>
                  <text y="-204.64" x="915.595" style={{fill: '#3c4a4c', fontSize: '6px', textAnchor: 'middle', fontFamily: 'sans-serif', fontWeight: 'bold', dominantBaseline: 'central'}}>5</text>
                  
                  <circle style={{fill: '#b4ae98', stroke: '#3c4a4c', strokeWidth: 0.353}} cx="923.397" cy="-253.3" r="5"/>
                  <text y="-253.3" x="923.397" style={{fill: '#3c4a4c', fontSize: '6px', textAnchor: 'middle', fontFamily: 'sans-serif', fontWeight: 'bold', dominantBaseline: 'central'}}>6</text> */}
                
                  {/* <circle style={{fill: '#b4ae98', stroke: '#3c4a4c', strokeWidth: 0.353}} cx="882.152" cy="-263.98" r="5"/>
                  <text y="-263.98" x="882.152" style={{fill: '#3c4a4c', fontSize: '6px', textAnchor: 'middle', fontFamily: 'sans-serif', fontWeight: 'bold', dominantBaseline: 'central'}}>7</text>
                  
                  <circle style={{fill: '#b4ae98', stroke: '#3c4a4c', strokeWidth: 0.353}} cx="892.951" cy="-245.608" r="5"/>
                  <text y="-245.608" x="892.951" style={{fill: '#3c4a4c', fontSize: '6px', textAnchor: 'middle', fontFamily: 'sans-serif', fontWeight: 'bold', dominantBaseline: 'central'}}>8</text>
                  
                  <circle style={{fill: '#b4ae98', stroke: '#3c4a4c', strokeWidth: 0.353}} cx="861.83" cy="-262.8" r="5"/>
                  <text y="-262.8" x="861.83" style={{fill: '#3c4a4c', fontSize: '6px', textAnchor: 'middle', fontFamily: 'sans-serif', fontWeight: 'bold', dominantBaseline: 'central'}}>9</text>
                  
                  <circle style={{fill: '#b4ae98', stroke: '#3c4a4c', strokeWidth: 0.353}} cx="871.759" cy="-185.468" r="5"/>
                  <text y="-185.468" x="871.759" style={{fill: '#3c4a4c', fontSize: '5px', textAnchor: 'middle', fontFamily: 'sans-serif', fontWeight: 'bold', dominantBaseline: 'central'}}>10</text>
                  
                  <circle style={{fill: '#b4ae98', stroke: '#3c4a4c', strokeWidth: 0.353}} cx="819.783" cy="-175.619" r="5"/>
                  <text y="-175.619" x="819.783" style={{fill: '#3c4a4c', fontSize: '5px', textAnchor: 'middle', fontFamily: 'sans-serif', fontWeight: 'bold', dominantBaseline: 'central'}}>11</text>
                  
                  <circle style={{fill: '#b4ae98', stroke: '#3c4a4c', strokeWidth: 0.353}} cx="810.639" cy="-141.836" r="5"/>
                  <text y="-141.836" x="810.639" style={{fill: '#3c4a4c', fontSize: '5px', textAnchor: 'middle', fontFamily: 'sans-serif', fontWeight: 'bold', dominantBaseline: 'central'}}>12</text>
                  
                  <circle style={{fill: '#b4ae98', stroke: '#3c4a4c', strokeWidth: 0.353}} cx="912.386" cy="-89.576" r="5"/>
                  <text y="-89.576" x="912.386" style={{fill: '#3c4a4c', fontSize: '5px', textAnchor: 'middle', fontFamily: 'sans-serif', fontWeight: 'bold', dominantBaseline: 'central'}}>13</text>
                  
                  <circle style={{fill: '#b4ae98', stroke: '#3c4a4c', strokeWidth: 0.353}} cx="1098.296" cy="-83.385" r="5"/>
                  <text y="-83.385" x="1098.296" style={{fill: '#3c4a4c', fontSize: '5px', textAnchor: 'middle', fontFamily: 'sans-serif', fontWeight: 'bold', dominantBaseline: 'central'}}>14</text>
                  
                  <circle style={{fill: '#b4ae98', stroke: '#3c4a4c', strokeWidth: 0.353}} cx="1074.489" cy="-116.863" r="5"/>
                  <text y="-116.863" x="1074.489" style={{fill: '#3c4a4c', fontSize: '5px', textAnchor: 'middle', fontFamily: 'sans-serif', fontWeight: 'bold', dominantBaseline: 'central'}}>15</text>
                  
                  <circle style={{fill: '#b4ae98', stroke: '#3c4a4c', strokeWidth: 0.353}} cx="1116.858" cy="-114.551" r="5"/>
                  <text y="-114.551" x="1116.858" style={{fill: '#3c4a4c', fontSize: '5px', textAnchor: 'middle', fontFamily: 'sans-serif', fontWeight: 'bold', dominantBaseline: 'central'}}>16</text>
                  
                  <circle style={{fill: '#b4ae98', stroke: '#3c4a4c', strokeWidth: 0.353}} cx="1107.076" cy="-213.366" r="5"/>
                  <text y="-213.366" x="1107.076" style={{fill: '#3c4a4c', fontSize: '5px', textAnchor: 'middle', fontFamily: 'sans-serif', fontWeight: 'bold', dominantBaseline: 'central'}}>17</text> */}
               

              </svg>

              {/* Interactive Achievement Markers */}
              <div className="absolute inset-0">
                {achievements.map((achievement, index) => (
                  <div
                    className={`absolute transform -translate-x-1/2 -translate-y-1/2 group hover:z-50 ${achievement.link ? 'cursor-pointer' : ''}`}
                    style={achievement.position}
                    onClick={achievement.link ? () => window.open(achievement.link, '_blank') : undefined}
                  >
                    <div className="relative">
                      {/* Marker */}
                      <div className="w-8 h-8 bg-[#b4ae98] rounded-full border-4 border-white shadow-lg hover:scale-125 transition-all duration-300 relative z-10"></div>
                      
                      {/* Achievement number */}
                      <div className="absolute inset-0 flex items-center justify-center text-[#3c4a4c] font-bold text-xs z-20">
                        {index + 1}
                      </div>
                      
                      {/* Interactive Tooltip */}
                      <div className="fixed opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-[9999]" 
                           style={{
                             bottom: 'auto',
                             top: '50%',
                             left: '50%',
                             transform: 'translate(-50%, calc(-100% - 2rem))'
                           }}>
                        <div className="bg-black/95 backdrop-blur-sm p-4 rounded-lg border border-[#b4ae98]/50 min-w-64 shadow-2xl">
                          <div className="flex items-center mb-2">
                            <div className={`p-1 rounded mr-2 ${
                              achievement.type === 'education' ? 'bg-[#b4ae98]/20 text-[#b4ae98]' :
                              achievement.type === 'work' ? 'bg-[#66706a]/20 text-[#66706a]' :
                              'bg-white/20 text-white'
                            }`}>
                              {getAchievementIcon(achievement.type)}
                            </div>
                            <span className="font-bold text-[#b4ae98] text-sm">{achievement.year}</span>
                          </div>
                          <h3 className="font-bold text-white mb-2 text-sm">{achievement.title}</h3>
                          <p className="text-gray-300 text-xs leading-relaxed">{achievement.description}</p>
                        </div>
                        {/* Tooltip arrow */}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-l-8 border-r-8 border-t-8 border-transparent border-t-black/95"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Achievement Cards Section */}
        <div className="bg-[#3c4a4c]/30 rounded-xl p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-[#b4ae98] font-mono">CAREER MILESTONES</h3>
            <p className="text-gray-400 text-sm mt-2">Key achievements and experience highlights</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {achievements.map((achievement, index) => (
              <div
                key={achievement.id}
                className={`bg-[#3c4a4c]/60 backdrop-blur-sm p-4 rounded-lg border border-[#b4ae98]/20 hover:border-[#b4ae98]/50 transition-all duration-300 hover:transform hover:scale-105 ${achievement.link ? 'cursor-pointer' : ''}`}
                onClick={achievement.link ? () => window.open(achievement.link, '_blank') : undefined}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-[#b4ae98] rounded-full flex items-center justify-center text-[#3c4a4c] font-bold text-sm">
                      {index + 1}
                    </div>
                  </div>
                  
                  <div className="flex-grow">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className={`p-1 rounded ${
                        achievement.type === 'education' ? 'bg-[#b4ae98]/20 text-[#b4ae98]' :
                        achievement.type === 'work' ? 'bg-[#66706a]/20 text-[#66706a]' :
                        'bg-white/20 text-white'
                      }`}>
                        {getAchievementIcon(achievement.type)}
                      </div>
                      <span className="text-xs text-gray-400 font-mono">{achievement.year}</span>
                    </div>
                    
                    <h4 className="font-semibold text-white mb-1 text-sm">{achievement.title}</h4>
                    <p className="text-gray-300 text-xs leading-relaxed">{achievement.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Achievement Legend */}
        <div className="mt-8 flex flex-wrap justify-center gap-6">
          <div className="flex items-center space-x-2 bg-[#3c4a4c]/50 px-4 py-2 rounded-full">
            <GraduationCap className="w-4 h-4 text-[#b4ae98]" />
            <span className="text-sm">Education</span>
          </div>
          <div className="flex items-center space-x-2 bg-[#3c4a4c]/50 px-4 py-2 rounded-full">
            <Briefcase className="w-4 h-4 text-[#66706a]" />
            <span className="text-sm">Work Experience</span>
          </div>
          <div className="flex items-center space-x-2 bg-[#3c4a4c]/50 px-4 py-2 rounded-full">
            <Trophy className="w-4 h-4 text-white" />
            <span className="text-sm">Personal Projects</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 text-center border-t border-[#b4ae98]/30 bg-black/30">
        <p className="text-gray-400 mb-4">
          Ready to race together on your next project?
        </p>
        <a
          href="mailto:yongliu1212@gmail.com"
          className="inline-block bg-[#b4ae98] hover:bg-[#66706a] text-black px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-[#b4ae98]/50"
        >
          GET IN TOUCH
        </a>
      </footer>
    </div>
  );
};

export default F1Portfolio;